import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import SEO from "../components/SEO";
import { NavLink } from "react-router-dom";
import { logActivity } from "../utils/activity";
import pricingData from "../data/pricing.json";
import authCopy from "../data/private/auth.json";

export default function Pricing() {
  const [modalPlan, setModalPlan] = useState(null);

  return (
    <div className="bg-slate-50">
      <SEO
        title={pricingData.seo?.title}
        description={pricingData.seo?.description}
        ogType="website"
        ogImage={pricingData.seo?.ogImage}
      />
      <section className="section-padding mx-auto w-full max-w-6xl">
        {/* <!-- Replace with dynamic content from JSON --> */}
        <p className="text-sm uppercase tracking-[0.2em] text-amber-600">
          Pricing
        </p>
        <h1 className="page-title mt-3">
          {pricingData.headline}
        </h1>
        <p className="mt-4 text-lg text-slate-600">
          {pricingData.seo?.description}
        </p>
        <NavLink
          to="/contact"
          className="mt-4 inline-block text-sm font-semibold text-amber-600 transition hover:text-amber-500"
          onClick={() =>
            logActivity("internal_link", { from: "pricing", to: "contact" })
          }
        >
          {pricingData.contactLinkLabel}
        </NavLink>
      </section>

      <section className="section-padding bg-white">
        <div className="mx-auto grid w-full max-w-6xl gap-6 md:grid-cols-2">
          {/* <!-- Replace with dynamic content from JSON --> */}
          {pricingData.plans.map((plan) => (
            <motion.div
              key={plan.name}
              className={`card p-8 ${
                plan.highlight ? "border-amber-500 shadow-xl shadow-amber-100" : ""
              }`}
              whileHover={{ y: -6 }}
            >
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-slate-900">
                  {plan.name}
                </h3>
                {plan.highlight && (
                  <span className="rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white">
                    Best Value
                  </span>
                )}
              </div>
              <p className="mt-4 text-3xl font-semibold text-slate-900">
                {plan.price}
              </p>
              <p className="mt-2 text-sm text-slate-600">{plan.description}</p>
              <ul className="mt-6 space-y-2 text-sm text-slate-600">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                className="btn btn-primary mt-6 w-full"
                onClick={() => {
                  logActivity("pricing_select", {
                    plan: plan.name,
                    price: plan.price,
                  });
                  setModalPlan(plan);
                  logActivity("pricing_modal_open", { plan: plan.name });
                }}
              >
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="section-padding">
        <div className="mx-auto w-full max-w-6xl">
          <h2 className="text-2xl font-semibold text-slate-900">
            Frequently asked questions
          </h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {/* <!-- Replace with dynamic content from JSON --> */}
            {pricingData.faqs.map((faq) => (
              <div key={faq.question} className="card p-6">
                <p className="text-sm font-semibold text-slate-900">
                  {faq.question}
                </p>
                <p className="mt-2 text-sm text-slate-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {modalPlan && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setModalPlan(null);
              logActivity("pricing_modal_close", { plan: modalPlan.name });
            }}
          >
            <motion.div
              className="card w-full max-w-md p-6 shadow-lg"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.2 }}
              onClick={(event) => event.stopPropagation()}
            >
              <h2 className="text-xl font-semibold text-slate-900">
                {pricingData.subtitle}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {authCopy.otp.subtitle}
              </p>
              <div className="mt-4 card p-4">
                <p className="text-sm font-semibold text-slate-900">
                  {modalPlan.name}
                </p>
                <p className="text-sm text-slate-600">{modalPlan.price}</p>
              </div>
              <NavLink
                to="/signup"
                className="btn btn-primary mt-4 w-full"
                onClick={() => {
                  setModalPlan(null);
                  logActivity("pricing_modal_cta", { plan: modalPlan.name });
                }}
              >
                {authCopy.signup.submitLabel}
              </NavLink>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
